import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  Building2, Network, Users, UserPlus, Trash2, ChevronRight, Plus, FolderTree
} from 'lucide-react';

export default function OrgManagement() {
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('hierarchy');

  // Dialog states
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showCreateVertical, setShowCreateVertical] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showAssignUser, setShowAssignUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [orgName, setOrgName] = useState('');
  const [vertName, setVertName] = useState('');
  const [vertOrgId, setVertOrgId] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamVertId, setTeamVertId] = useState('');
  const [teamOrgId, setTeamOrgId] = useState('');
  const [teamType, setTeamType] = useState('team');
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user', org_id: '', vertical_id: '', team_id: '' });
  const [assignData, setAssignData] = useState({ org_id: '', vertical_id: '', team_id: '' });

  // Expanded org state
  const [expandedOrgs, setExpandedOrgs] = useState({});
  const [expandedVerts, setExpandedVerts] = useState({});

  // Memoized filtered lists for dialog dropdowns
  const teamDialogVerticals = useMemo(() => verticals.filter(v => v.org_id === teamOrgId), [verticals, teamOrgId]);
  const newUserVerticals = useMemo(() => verticals.filter(v => v.org_id === newUser.org_id), [verticals, newUser.org_id]);
  const newUserTeams = useMemo(() => teams.filter(t => t.vertical_id === newUser.vertical_id), [teams, newUser.vertical_id]);
  const assignVerticals = useMemo(() => verticals.filter(v => v.org_id === assignData.org_id), [verticals, assignData.org_id]);
  const assignTeams = useMemo(() => teams.filter(t => t.vertical_id === assignData.vertical_id), [teams, assignData.vertical_id]);

  const fetchAll = useCallback(async () => {
    try {
      const api = axiosAuth();
      const [oRes, vRes, tRes, uRes] = await Promise.all([
        api.get('/admin/orgs'),
        api.get('/admin/verticals'),
        api.get('/admin/teams'),
        api.get('/admin/users'),
      ]);
      setOrgs(oRes.data.orgs || []);
      setVerticals(vRes.data.verticals || []);
      setTeams(tRes.data.teams || []);
      setUsers(uRes.data.users || []);
    } catch (err) {
      if (err.response?.status === 403) { toast.error('Admin access required'); navigate('/'); }
      else toast.error('Failed to load data');
    } finally { setLoading(false); }
  }, [axiosAuth, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createOrg = async () => {
    if (!orgName.trim()) return toast.error('Name required');
    try {
      await axiosAuth().post('/admin/orgs', { name: orgName.trim() });
      toast.success('Organization created');
      setOrgName(''); setShowCreateOrg(false); fetchAll();
    } catch { toast.error('Failed to create organization'); }
  };

  const deleteOrg = async (orgId) => {
    if (!window.confirm('Delete this organization? All verticals and teams under it will be removed.')) return;
    try {
      await axiosAuth().delete(`/admin/orgs/${orgId}`);
      toast.success('Organization deleted'); fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const createVertical = async () => {
    if (!vertName.trim() || !vertOrgId) return toast.error('Name and organization required');
    try {
      await axiosAuth().post('/admin/verticals', { name: vertName.trim(), org_id: vertOrgId });
      toast.success('Vertical created');
      setVertName(''); setVertOrgId(''); setShowCreateVertical(false); fetchAll();
    } catch { toast.error('Failed to create vertical'); }
  };

  const deleteVertical = async (vId) => {
    if (!window.confirm('Delete this vertical? All teams under it will be removed.')) return;
    try {
      await axiosAuth().delete(`/admin/verticals/${vId}`);
      toast.success('Vertical deleted'); fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const createTeam = async () => {
    if (!teamName.trim() || !teamVertId || !teamOrgId) return toast.error('All fields required');
    try {
      await axiosAuth().post('/admin/teams', { name: teamName.trim(), vertical_id: teamVertId, org_id: teamOrgId, team_type: teamType });
      toast.success(`${teamType === 'cohort' ? 'Cohort' : 'Team'} created`);
      setTeamName(''); setTeamVertId(''); setTeamOrgId(''); setTeamType('team');
      setShowCreateTeam(false); fetchAll();
    } catch { toast.error('Failed to create team'); }
  };

  const deleteTeam = async (tId) => {
    if (!window.confirm('Delete this team/cohort?')) return;
    try {
      await axiosAuth().delete(`/admin/teams/${tId}`);
      toast.success('Deleted'); fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) return toast.error('Name, email, and password required');
    try {
      await axiosAuth().post('/admin/users/create', newUser);
      toast.success('User created');
      setNewUser({ name: '', email: '', password: '', role: 'user', org_id: '', vertical_id: '', team_id: '' });
      setShowCreateUser(false); fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const assignUser = async () => {
    if (!selectedUser) return;
    try {
      await axiosAuth().put(`/admin/users/${selectedUser.id}/assign`, assignData);
      toast.success('User assigned');
      setShowAssignUser(false); setSelectedUser(null);
      setAssignData({ org_id: '', vertical_id: '', team_id: '' }); fetchAll();
    } catch { toast.error('Failed to assign user'); }
  };

  const toggleOrg = (orgId) => setExpandedOrgs(p => ({ ...p, [orgId]: !p[orgId] }));
  const toggleVert = (vId) => setExpandedVerts(p => ({ ...p, [vId]: !p[vId] }));

  const getVerticalsForOrg = (orgId) => verticals.filter(v => v.org_id === orgId);
  const getTeamsForVertical = (vId) => teams.filter(t => t.vertical_id === vId);
  const getUsersForTeam = (teamId) => users.filter(u => u.team_id === teamId);
  const getUnassignedUsers = () => users.filter(u => !u.org_id || (!u.vertical_id && !u.team_id));

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="heritage-card p-8 text-center"><div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="font-body text-sm">Loading...</p></div>
    </div>
  );

  const tabs = [
    { id: 'hierarchy', label: 'Hierarchy', icon: FolderTree },
    { id: 'users', label: 'Users', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="org-management-page">
      <Navbar showBack backTo="/admin" backLabel="Organization Management" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Organization Management</h1>
            <p className="font-body text-sm text-muted-foreground mt-1">Manage your organizational hierarchy and user assignments</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="create-org-btn" onClick={() => setShowCreateOrg(true)}
              className="bg-brand text-[#333333] gap-1.5 h-8 px-3 text-xs font-medium rounded-lg hover:opacity-90">
              <Plus className="w-3.5 h-3.5" /> Organization
            </Button>
            <Button data-testid="create-user-btn" onClick={() => setShowCreateUser(true)}
              className="bg-info text-white gap-1.5 h-8 px-3 text-xs font-medium rounded-lg hover:opacity-90">
              <UserPlus className="w-3.5 h-3.5" /> User
            </Button>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit mb-6" data-testid="org-tabs">
          {tabs.map(t => (
            <button key={t.id} data-testid={`tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === t.id ? 'bg-surface text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'hierarchy' && (<>
          <div className="space-y-3" data-testid="hierarchy-view">
            {orgs.length === 0 ? (
              <div className="heritage-card p-8 text-center">
                <Building2 className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <p className="font-heading text-sm font-bold mb-1">No Organizations Yet</p>
                <p className="font-body text-xs text-muted-foreground mb-4">Create your first organization to start building the hierarchy</p>
                <Button onClick={() => setShowCreateOrg(true)} className="bg-brand text-[#333333] gap-1.5 h-8 px-4 text-xs font-medium rounded-lg hover:opacity-90">
                  <Plus className="w-3.5 h-3.5" /> Create Organization
                </Button>
              </div>
            ) : (
              orgs.map(org => {
                const orgVerts = getVerticalsForOrg(org.id);
                const orgUsers = users.filter(u => u.org_id === org.id);
                return (
                  <div key={org.id} className="heritage-card overflow-hidden" data-testid={`org-${org.id}`}>
                    <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => toggleOrg(org.id)}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-brand/10 rounded-lg flex items-center justify-center">
                          <Building2 className="w-4.5 h-4.5 text-brand" />
                        </div>
                        <div>
                          <p className="font-heading text-sm font-bold">{org.name}</p>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="font-body text-[11px] text-muted-foreground">{orgVerts.length} vertical{orgVerts.length !== 1 ? 's' : ''}</span>
                            <span className="font-body text-[11px] text-muted-foreground">{orgUsers.length} user{orgUsers.length !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button onClick={(e) => { e.stopPropagation(); setVertOrgId(org.id); setShowCreateVertical(true); }}
                          className="h-7 px-2 text-[10px] bg-transparent border border-border text-foreground hover:bg-muted shadow-none gap-1" data-testid={`add-vertical-${org.id}`}>
                          <Plus className="w-3 h-3" /> Vertical
                        </Button>
                        <button onClick={(e) => { e.stopPropagation(); deleteOrg(org.id); }}
                          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors"
                          data-testid={`delete-org-${org.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedOrgs[org.id] ? 'rotate-90' : ''}`} />
                      </div>
                    </div>

                    {expandedOrgs[org.id] && (
                      <div className="border-t border-border">
                        {orgVerts.length === 0 ? (
                          <div className="px-4 py-3 pl-12 text-xs text-muted-foreground font-body">No verticals yet</div>
                        ) : (
                          orgVerts.map(vert => {
                            const vertTeams = getTeamsForVertical(vert.id);
                            return (
                              <div key={vert.id} data-testid={`vertical-${vert.id}`}>
                                <div className="px-4 py-3 pl-10 flex items-center justify-between hover:bg-muted/30 transition-colors cursor-pointer border-b border-border/50"
                                  onClick={() => toggleVert(vert.id)}>
                                  <div className="flex items-center gap-2.5">
                                    <Network className="w-3.5 h-3.5 text-info" />
                                    <span className="font-heading text-xs font-bold">{vert.name}</span>
                                    <Badge className="bg-info/10 text-info border-none text-[10px] px-1.5 py-0">{vertTeams.length} team{vertTeams.length !== 1 ? 's' : ''}</Badge>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button onClick={(e) => { e.stopPropagation(); setTeamOrgId(org.id); setTeamVertId(vert.id); setShowCreateTeam(true); }}
                                      className="h-6 px-2 text-[10px] bg-transparent border border-border text-foreground hover:bg-muted shadow-none gap-1" data-testid={`add-team-${vert.id}`}>
                                      <Plus className="w-2.5 h-2.5" /> Team
                                    </Button>
                                    <button onClick={(e) => { e.stopPropagation(); deleteVertical(vert.id); }}
                                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors">
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                    <ChevronRight className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${expandedVerts[vert.id] ? 'rotate-90' : ''}`} />
                                  </div>
                                </div>

                                {expandedVerts[vert.id] && vertTeams.length > 0 && (
                                  <div className="bg-muted/20">
                                    {vertTeams.map(team => {
                                      const teamUsers = getUsersForTeam(team.id);
                                      return (
                                        <div key={team.id} className="px-4 py-2.5 pl-16 flex items-center justify-between border-b border-border/30" data-testid={`team-${team.id}`}>
                                          <div className="flex items-center gap-2">
                                            <Users className="w-3 h-3 text-success" />
                                            <span className="font-body text-xs font-medium">{team.name}</span>
                                            <Badge className={`${team.team_type === 'cohort' ? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'} border-none text-[10px] px-1.5 py-0`}>
                                              {team.team_type || 'team'}
                                            </Badge>
                                            <span className="font-body text-[10px] text-muted-foreground">{teamUsers.length} member{teamUsers.length !== 1 ? 's' : ''}</span>
                                          </div>
                                          <button onClick={() => deleteTeam(team.id)}
                                            className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors">
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Unassigned Users in Hierarchy view */}
          {getUnassignedUsers().length > 0 && (
            <div className="heritage-card p-4 mt-4 border-l-4 border-warning">
              <p className="font-heading text-xs font-bold text-warning mb-3 flex items-center gap-1.5">
                <UserPlus className="w-3.5 h-3.5" /> Unassigned Users ({getUnassignedUsers().length})
              </p>
              <div className="space-y-1.5">
                {getUnassignedUsers().map(u => (
                  <UserRow key={u.id} user={u} orgs={orgs} verticals={verticals} teams={teams}
                    onAssign={() => { setSelectedUser(u); setAssignData({ org_id: u.org_id || '', vertical_id: u.vertical_id || '', team_id: u.team_id || '' }); setShowAssignUser(true); }} />
                ))}
              </div>
            </div>
          )}
        </>)}

        {activeTab === 'users' && (
          <div className="space-y-2" data-testid="users-view">
            {/* Unassigned users section */}
            {getUnassignedUsers().length > 0 && (
              <div className="mb-4">
                <p className="font-heading text-xs font-bold text-warning mb-2 flex items-center gap-1.5">
                  <UserPlus className="w-3.5 h-3.5" /> Unassigned Users ({getUnassignedUsers().length})
                </p>
                {getUnassignedUsers().map(u => (
                  <UserRow key={u.id} user={u} orgs={orgs} onAssign={() => { setSelectedUser(u); setAssignData({ org_id: '', vertical_id: '', team_id: '' }); setShowAssignUser(true); }} />
                ))}
              </div>
            )}

            {/* All users */}
            <p className="font-heading text-xs font-bold text-muted-foreground mb-2">All Users ({users.length})</p>
            {users.map(u => (
              <UserRow key={u.id} user={u} orgs={orgs} verticals={verticals} teams={teams}
                onAssign={() => { setSelectedUser(u); setAssignData({ org_id: u.org_id || '', vertical_id: u.vertical_id || '', team_id: u.team_id || '' }); setShowAssignUser(true); }} />
            ))}
          </div>
        )}
      </main>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateOrg} onOpenChange={setShowCreateOrg}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-sm" data-testid="create-org-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Create Organization</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Add a new organization to the hierarchy</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <input data-testid="org-name-input" type="text" value={orgName} onChange={e => setOrgName(e.target.value)}
              className="heritage-input w-full text-sm" placeholder="Organization name" />
            <div className="flex gap-2">
              <Button onClick={() => setShowCreateOrg(false)} className="flex-1 h-9 bg-transparent border border-border text-foreground hover:bg-muted text-sm rounded-lg shadow-none">Cancel</Button>
              <Button data-testid="org-save-btn" onClick={createOrg} className="flex-1 h-9 bg-brand text-[#333333] text-sm rounded-lg hover:opacity-90">Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Vertical Dialog */}
      <Dialog open={showCreateVertical} onOpenChange={setShowCreateVertical}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-sm" data-testid="create-vertical-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Create Vertical</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Add a vertical under an organization</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <input data-testid="vertical-name-input" type="text" value={vertName} onChange={e => setVertName(e.target.value)}
              className="heritage-input w-full text-sm" placeholder="Vertical name (e.g., Engineering)" />
            <Select value={vertOrgId} onValueChange={setVertOrgId}>
              <SelectTrigger data-testid="vertical-org-select" className="w-full border border-border h-9 font-body text-sm bg-surface">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent className="border border-border shadow-modal bg-surface">
                {orgs.map(o => <SelectItem key={o.id} value={o.id} className="font-body text-sm">{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button onClick={() => setShowCreateVertical(false)} className="flex-1 h-9 bg-transparent border border-border text-foreground hover:bg-muted text-sm rounded-lg shadow-none">Cancel</Button>
              <Button data-testid="vertical-save-btn" onClick={createVertical} className="flex-1 h-9 bg-brand text-[#333333] text-sm rounded-lg hover:opacity-90">Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Team Dialog */}
      <Dialog open={showCreateTeam} onOpenChange={setShowCreateTeam}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-sm" data-testid="create-team-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Create Team / Cohort</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Add a team or cohort under a vertical</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <input data-testid="team-name-input" type="text" value={teamName} onChange={e => setTeamName(e.target.value)}
              className="heritage-input w-full text-sm" placeholder="Team name" />
            <Select value={teamOrgId} onValueChange={(val) => { setTeamOrgId(val); setTeamVertId(''); }}>
              <SelectTrigger className="w-full border border-border h-9 font-body text-sm bg-surface">
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent className="border border-border shadow-modal bg-surface">
                {orgs.map(o => <SelectItem key={o.id} value={o.id} className="font-body text-sm">{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={teamVertId} onValueChange={setTeamVertId}>
              <SelectTrigger data-testid="team-vertical-select" className="w-full border border-border h-9 font-body text-sm bg-surface">
                <SelectValue placeholder="Select vertical" />
              </SelectTrigger>
              <SelectContent className="border border-border shadow-modal bg-surface">
                {teamDialogVerticals.map(v => <SelectItem key={v.id} value={v.id} className="font-body text-sm">{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={teamType} onValueChange={setTeamType}>
              <SelectTrigger data-testid="team-type-select" className="w-full border border-border h-9 font-body text-sm bg-surface">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border border-border shadow-modal bg-surface">
                <SelectItem value="team" className="font-body text-sm">Team</SelectItem>
                <SelectItem value="cohort" className="font-body text-sm">Cohort</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button onClick={() => setShowCreateTeam(false)} className="flex-1 h-9 bg-transparent border border-border text-foreground hover:bg-muted text-sm rounded-lg shadow-none">Cancel</Button>
              <Button data-testid="team-save-btn" onClick={createTeam} className="flex-1 h-9 bg-brand text-[#333333] text-sm rounded-lg hover:opacity-90">Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Create User</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Add a new user and optionally assign to an organization</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <input data-testid="user-name-input" type="text" value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))}
              className="heritage-input w-full text-sm" placeholder="Full name" />
            <input data-testid="user-email-input" type="email" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))}
              className="heritage-input w-full text-sm" placeholder="Email" />
            <input data-testid="user-password-input" type="password" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))}
              className="heritage-input w-full text-sm" placeholder="Password" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={newUser.role} onValueChange={val => setNewUser(p => ({ ...p, role: val }))}>
                <SelectTrigger className="border border-border h-9 font-body text-sm bg-surface"><SelectValue /></SelectTrigger>
                <SelectContent className="border border-border shadow-modal bg-surface">
                  <SelectItem value="user" className="font-body text-sm">User</SelectItem>
                  <SelectItem value="admin" className="font-body text-sm">Admin</SelectItem>
                </SelectContent>
              </Select>
              <Select value={newUser.org_id || '_none'} onValueChange={val => setNewUser(p => ({ ...p, org_id: val === '_none' ? '' : val, vertical_id: '', team_id: '' }))}>
                <SelectTrigger className="border border-border h-9 font-body text-sm bg-surface"><SelectValue placeholder="Organization" /></SelectTrigger>
                <SelectContent className="border border-border shadow-modal bg-surface">
                  <SelectItem value="_none" className="font-body text-sm">No Organization</SelectItem>
                  {orgs.map(o => <SelectItem key={o.id} value={o.id} className="font-body text-sm">{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {newUser.org_id && (
              <div className="grid grid-cols-2 gap-3">
                <Select value={newUser.vertical_id || '_none'} onValueChange={val => setNewUser(p => ({ ...p, vertical_id: val === '_none' ? '' : val, team_id: '' }))}>
                  <SelectTrigger className="border border-border h-9 font-body text-sm bg-surface"><SelectValue placeholder="Vertical" /></SelectTrigger>
                  <SelectContent className="border border-border shadow-modal bg-surface">
                    <SelectItem value="_none" className="font-body text-sm">No Vertical</SelectItem>
                    {newUserVerticals.map(v => <SelectItem key={v.id} value={v.id} className="font-body text-sm">{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {newUser.vertical_id && (
                  <Select value={newUser.team_id || '_none'} onValueChange={val => setNewUser(p => ({ ...p, team_id: val === '_none' ? '' : val }))}>
                    <SelectTrigger className="border border-border h-9 font-body text-sm bg-surface"><SelectValue placeholder="Team" /></SelectTrigger>
                    <SelectContent className="border border-border shadow-modal bg-surface">
                      <SelectItem value="_none" className="font-body text-sm">No Team</SelectItem>
                      {newUserTeams.map(t => <SelectItem key={t.id} value={t.id} className="font-body text-sm">{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button onClick={() => setShowCreateUser(false)} className="flex-1 h-9 bg-transparent border border-border text-foreground hover:bg-muted text-sm rounded-lg shadow-none">Cancel</Button>
              <Button data-testid="user-save-btn" onClick={createUser} className="flex-1 h-9 bg-brand text-[#333333] text-sm rounded-lg hover:opacity-90">Create User</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog open={showAssignUser} onOpenChange={(open) => { if (!open) { setShowAssignUser(false); setSelectedUser(null); } }}>
        <DialogContent className="border border-border shadow-modal bg-surface max-w-sm" data-testid="assign-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold">Assign User</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground">Assign {selectedUser?.name} to an organization, vertical, and team</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Select value={assignData.org_id || '_none'} onValueChange={val => setAssignData(p => ({ ...p, org_id: val === '_none' ? '' : val, vertical_id: '', team_id: '' }))}>
              <SelectTrigger data-testid="assign-org-select" className="w-full border border-border h-9 font-body text-sm bg-surface"><SelectValue placeholder="Organization" /></SelectTrigger>
              <SelectContent className="border border-border shadow-modal bg-surface">
                <SelectItem value="_none" className="font-body text-sm">None</SelectItem>
                {orgs.map(o => <SelectItem key={o.id} value={o.id} className="font-body text-sm">{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {assignData.org_id && (
              <Select value={assignData.vertical_id || '_none'} onValueChange={val => setAssignData(p => ({ ...p, vertical_id: val === '_none' ? '' : val, team_id: '' }))}>
                <SelectTrigger className="w-full border border-border h-9 font-body text-sm bg-surface"><SelectValue placeholder="Vertical" /></SelectTrigger>
                <SelectContent className="border border-border shadow-modal bg-surface">
                  <SelectItem value="_none" className="font-body text-sm">None</SelectItem>
                  {verticals.filter(v => v.org_id === assignData.org_id).map(v => <SelectItem key={v.id} value={v.id} className="font-body text-sm">{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {assignData.vertical_id && (
              <Select value={assignData.team_id || '_none'} onValueChange={val => setAssignData(p => ({ ...p, team_id: val === '_none' ? '' : val }))}>
                <SelectTrigger className="w-full border border-border h-9 font-body text-sm bg-surface"><SelectValue placeholder="Team" /></SelectTrigger>
                <SelectContent className="border border-border shadow-modal bg-surface">
                  <SelectItem value="_none" className="font-body text-sm">None</SelectItem>
                  {assignTeams.map(t => <SelectItem key={t.id} value={t.id} className="font-body text-sm">{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="flex gap-2">
              <Button onClick={() => { setShowAssignUser(false); setSelectedUser(null); }} className="flex-1 h-9 bg-transparent border border-border text-foreground hover:bg-muted text-sm rounded-lg shadow-none">Cancel</Button>
              <Button data-testid="assign-save-btn" onClick={assignUser} className="flex-1 h-9 bg-brand text-[#333333] text-sm rounded-lg hover:opacity-90">Assign</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserRow({ user, orgs, verticals, teams, onAssign }) {
  const orgName = orgs?.find(o => o.id === user.org_id)?.name;
  const vertName = verticals?.find(v => v.id === user.vertical_id)?.name;
  const teamName = teams?.find(t => t.id === user.team_id)?.name;

  return (
    <div className="heritage-card p-3 flex items-center gap-3 mb-1.5" data-testid={`user-row-${user.id}`}>
      <div className="w-8 h-8 bg-brand text-[#333333] rounded-full flex items-center justify-center font-heading font-bold text-xs flex-shrink-0">
        {user.name?.charAt(0)?.toUpperCase() || 'U'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-heading text-xs font-bold truncate">{user.name}</p>
        <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
          <span className="font-body text-[10px] text-muted-foreground">{user.email}</span>
          {orgName && <Badge className="bg-brand/10 text-brand border-none text-[9px] px-1 py-0">{orgName}</Badge>}
          {vertName && <Badge className="bg-info/10 text-info border-none text-[9px] px-1 py-0">{vertName}</Badge>}
          {teamName && <Badge className="bg-success/10 text-success border-none text-[9px] px-1 py-0">{teamName}</Badge>}
          <Badge className={`${['super_admin','org_admin'].includes(user.role) ? 'bg-accent/10 text-accent' : user.role === 'manager' ? 'bg-brand/10 text-brand' : 'bg-muted text-muted-foreground'} border-none text-[9px] px-1 py-0`}>{user.role?.replace('_',' ')}</Badge>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="text-right hidden sm:block">
          <p className="font-heading text-xs font-bold">{user.avg_score}/10</p>
          <p className="font-body text-[10px] text-muted-foreground">{user.completed_count} sims</p>
        </div>
        <button onClick={onAssign} data-testid={`assign-btn-${user.id}`}
          className="w-7 h-7 border border-border rounded-md flex items-center justify-center hover:bg-brand/10 hover:border-brand/30 transition-colors" title="Assign to org/team">
          <Building2 className="w-3 h-3 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}
